import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { normalizeUserFacingErrorMessage } from '../utils/errorMessages';
import Toast from '../components/Toast';

export const ToastContext = createContext(null);

const KNOWN_TYPES = new Set(['success', 'error', 'warning', 'info']);

export const resolveToastArgs = (arg1, arg2, arg3) => {
  let message = arg1;
  let type = 'success';
  let options = {};

  if (typeof arg1 === 'string' && KNOWN_TYPES.has(arg1) && (typeof arg2 === 'string' || (arg2 && typeof arg2 === 'object'))) {
    type = arg1;
    if (typeof arg2 === 'string') {
      message = arg2;
    } else {
      message = arg2.message || arg2.detail || String(arg2);
      options = { ...arg2 };
    }
    if (arg3 && typeof arg3 === 'object') {
      options = { ...options, ...arg3 };
    }
  } else {
    message = arg1;
    if (typeof arg2 === 'string' && KNOWN_TYPES.has(arg2)) {
      type = arg2;
      options = (arg3 && typeof arg3 === 'object') ? arg3 : {};
    } else if (typeof arg2 === 'object' && arg2 !== null) {
      options = arg2;
      type = options.type || 'success';
    } else {
      type = typeof arg2 === 'string' ? arg2 : 'success';
      options = (arg3 && typeof arg3 === 'object') ? arg3 : {};
    }
  }

  return {
    message: normalizeUserFacingErrorMessage(message),
    type,
    options
  };
};

export const ToastProvider = ({ children, defaultDuration = 3200 }) => {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);
  const remainingTimeRef = useRef(defaultDuration);
  const startTimeRef = useRef(0);
  const isPausedRef = useRef(false);

  const clearExistingTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const hideToast = useCallback(() => {
    clearExistingTimer();
    isPausedRef.current = false;
    setToast(null);
  }, []);

  const pauseToastTimer = useCallback(() => {
    if (!toast || toast.persistent || isPausedRef.current) return;
    clearExistingTimer();
    const elapsed = Date.now() - startTimeRef.current;
    remainingTimeRef.current = Math.max(1000, remainingTimeRef.current - elapsed);
    isPausedRef.current = true;
  }, [toast]);

  const resumeToastTimer = useCallback(() => {
    if (!toast || toast.persistent || !isPausedRef.current) return;
    isPausedRef.current = false;
    startTimeRef.current = Date.now();
    clearExistingTimer();
    timerRef.current = setTimeout(() => {
      setToast(null);
    }, remainingTimeRef.current);
  }, [toast]);

  const showToast = useCallback((arg1, arg2, arg3) => {
    const { message, type, options } = resolveToastArgs(arg1, arg2, arg3);
    if (!message) return;

    clearExistingTimer();
    isPausedRef.current = false;

    const toastDuration = Number(options?.duration);
    const duration = Number.isFinite(toastDuration) && toastDuration > 0
      ? toastDuration
      : (type === 'error' ? Math.max(defaultDuration, 4000) : defaultDuration);

    remainingTimeRef.current = duration;
    startTimeRef.current = Date.now();

    const toastPayload = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      message,
      type,
      ...options
    };

    setToast(toastPayload);

    if (!options?.persistent) {
      timerRef.current = setTimeout(() => {
        setToast(null);
      }, duration);
    }
  }, [defaultDuration]);

  const value = {
    toast,
    showToast,
    hideToast,
    pauseToastTimer,
    resumeToastTimer
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast
        toast={toast}
        onDismiss={hideToast}
        onPause={pauseToastTimer}
        onResume={resumeToastTimer}
        isGlobal
      />
    </ToastContext.Provider>
  );
};

export const useToastContext = () => useContext(ToastContext);
